/**
 * <predictive-search> — suggestions for the search drawer.
 *
 * As the shopper types, the matching products (products only, SAVETTE-66 QA: no collections,
 * pages or articles) are fetched from Shopify's predictive search endpoint as a rendered section (sections/predictive-search.liquid) and swapped
 * into the results region, so the markup always comes from the server. The surrounding form still
 * submits to the full results page, which is what happens when scripting is unavailable.
 */
const DEBOUNCE_MS = 300;
const MIN_TERM_LENGTH = 2;

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-predictive-input]');
    this.results = this.querySelector('[data-predictive-results]');
    this.limit = Number(this.dataset.limit) || 4;
    this.input?.addEventListener('input', this.onInput);
    this.closest('site-drawer')?.addEventListener('drawer:close', this.reset);
  }

  onInput = () => {
    clearTimeout(this.timer);
    const term = this.input.value.trim();
    if (term.length < MIN_TERM_LENGTH) {
      this.reset();
      return;
    }
    this.timer = setTimeout(() => this.search(term), DEBOUNCE_MS);
  };

  reset = () => {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.controller = null;
    this.results.innerHTML = '';
    this.input.setAttribute('aria-expanded', 'false');
  };

  async search(term) {
    this.controller?.abort();
    this.controller = new AbortController();
    const root = window.Shopify?.routes?.root ?? '/';
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product',
      'resources[limit]': String(this.limit),
      section_id: 'predictive-search',
    });
    try {
      const response = await fetch(`${root}search/suggest?${params}`, { signal: this.controller.signal, cache: 'no-store' });
      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const content = parsed.querySelector('[data-predictive-content]');
      // A search that names a color can give a product one entry per matching color (SAVETTE-70),
      // so the section may render more entries than products; keep the drawer's number of them.
      content?.querySelectorAll('[data-predictive-entries] > li').forEach((entry, index) => {
        if (index >= this.limit) entry.remove();
      });
      this.results.innerHTML = content ? content.innerHTML : '';
      this.input.setAttribute('aria-expanded', String(Boolean(content?.textContent.trim())));
    } catch (error) {
      if (error.name !== 'AbortError') this.reset();
    }
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
